import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import type { Customer, PaymentMethod } from '@yui-receipt/shared'

export type PosItem = {
  key: string
  name: string
  qty: number
  unitPrice: number
  vatable: boolean
  free: boolean
}

type PosState = {
  customer: Customer | null
  items: PosItem[]
  paymentMethod: PaymentMethod
  vatRate: number
  discount: number
  receiptNo: string
  note: string | null
  setCustomer: (c: Customer | null) => void
  addItem: (partial?: Partial<PosItem>) => void
  updateItem: (key: string, patch: Partial<PosItem>) => void
  removeItem: (key: string) => void
  setPaymentMethod: (p: PaymentMethod) => void
  setDiscount: (d: number) => void
  setReceiptNo: (v: string) => void
  setNote: (n: string | null) => void
  reset: () => void
  subtotal: () => number
  subtotalVatable: () => number
  subtotalExempt: () => number
  vat: () => number
  total: () => number
}

let _k = 0
const nextKey = () => `it-${Date.now().toString(36)}-${++_k}`

// VAT model: a line's `vatable` checkbox means "price includes VAT".
// The typed unitPrice for vatable lines is gross; we back-extract net so
// the displayed "Subtotal" matches the printed tax invoice. Exempt lines
// (vatable=false) pass through unchanged.
export const usePos = create<PosState>()(
  immer((set, get) => ({
    customer: null,
    items: [],
    paymentMethod: 'cash',
    vatRate: 0.07,
    discount: 0,
    receiptNo: '',
    note: null,
    setCustomer(c) {
      set((s) => {
        s.customer = c
      })
    },
    addItem(partial) {
      set((s) => {
        s.items.push({
          key: nextKey(),
          name: partial?.name ?? '',
          qty: partial?.qty ?? 1,
          unitPrice: partial?.unitPrice ?? 0,
          vatable: partial?.vatable ?? false,
          free: partial?.free ?? false,
        })
      })
    },
    updateItem(key, patch) {
      set((s) => {
        const it = s.items.find((x) => x.key === key)
        if (!it) return
        if (patch.name !== undefined) it.name = patch.name
        if (patch.qty !== undefined) it.qty = Math.max(0, patch.qty)
        if (patch.unitPrice !== undefined) it.unitPrice = Math.max(0, patch.unitPrice)
        if (patch.vatable !== undefined) it.vatable = patch.vatable
        if (patch.free !== undefined) it.free = patch.free
      })
    },
    removeItem(key) {
      set((s) => {
        s.items = s.items.filter((x) => x.key !== key)
      })
    },
    setPaymentMethod(p) {
      set((s) => {
        s.paymentMethod = p
      })
    },
    setDiscount(d) {
      set((s) => {
        s.discount = Math.max(0, Number.isFinite(d) ? d : 0)
      })
    },
    setReceiptNo(v) {
      set((s) => {
        s.receiptNo = v
      })
    },
    setNote(n) {
      set((s) => {
        s.note = n
      })
    },
    reset() {
      set((s) => {
        s.customer = null
        s.items = []
        s.paymentMethod = 'cash'
        s.discount = 0
        s.receiptNo = ''
        s.note = null
      })
    },
    subtotalVatable() {
      const s = get()
      const gross = s.items
        .filter((it) => it.vatable && !it.free)
        .reduce((sum, it) => sum + it.qty * it.unitPrice, 0)
      return Math.round((gross / (1 + s.vatRate)) * 100) / 100
    },
    subtotalExempt() {
      return (
        Math.round(
          get()
            .items.filter((it) => !it.vatable && !it.free)
            .reduce((sum, it) => sum + it.qty * it.unitPrice, 0) * 100,
        ) / 100
      )
    },
    subtotal() {
      const s = get()
      return Math.round((s.subtotalVatable() + s.subtotalExempt()) * 100) / 100
    },
    vat() {
      const s = get()
      const gross = s.items
        .filter((it) => it.vatable && !it.free)
        .reduce((sum, it) => sum + it.qty * it.unitPrice, 0)
      const net = gross / (1 + s.vatRate)
      return Math.round((gross - net) * 100) / 100
    },
    total() {
      const s = get()
      const gross = Math.round((s.subtotal() + s.vat()) * 100) / 100
      const discount = Math.min(s.discount, gross)
      return Math.round((gross - discount) * 100) / 100
    },
  })),
)
